import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { authApi } from '@/api/auth';
import { handleAuthError } from '@/utils/errorHandler';
export const useUserStore = defineStore('user', () => {
    // 状态
    const token = ref('');
    const userInfo = ref(null);
    const isLoggedIn = ref(false);
    const userPermissions = ref(null); // 用户权限信息
    // 计算属性
    const isAdmin = computed(() => userInfo.value?.role === 'admin');
    const isManager = computed(() => userInfo.value?.role === 'admin' || userInfo.value?.role === 'manager');
    const userName = computed(() => userInfo.value?.realName || userInfo.value?.username || '');
    const userAvatar = computed(() => userInfo.value?.avatar || '');
    // 权限相关计算属性
    const hasSystemManagementPermission = computed(() => {
        if (isAdmin.value)
            return true;
        return userPermissions.value?.systemManagement || false;
    });
    const hasDeviceManagementPermission = computed(() => {
        if (isAdmin.value)
            return true;
        return userPermissions.value?.deviceManagement || false;
    });
    const managedGroups = computed(() => {
        if (isAdmin.value)
            return '*';
        return userPermissions.value?.groupManagement || [];
    });
    // 检查是否有组管理权限
    const hasGroupManagementPermission = computed(() => {
        if (isAdmin.value)
            return true;
        const groups = userPermissions.value?.groupManagement;
        return groups === '*' || (Array.isArray(groups) && groups.length > 0);
    });
    // 检查是否可以管理特定组
    const canManageGroup = (groupId) => {
        if (isAdmin.value)
            return true;
        const groups = userPermissions.value?.groupManagement;
        if (groups === '*')
            return true;
        if (Array.isArray(groups)) {
            return groups.includes(groupId);
        }
        return false;
    };
    // 初始化 - 从 localStorage 恢复状态
    const initAuth = () => {
        const savedToken = localStorage.getItem('nps_token');
        const savedUserInfo = localStorage.getItem('nps_user_info');
        const savedPermissions = localStorage.getItem('nps_user_permissions');
        if (savedToken && savedUserInfo) {
            try {
                token.value = savedToken;
                userInfo.value = JSON.parse(savedUserInfo);
                userPermissions.value = savedPermissions ? JSON.parse(savedPermissions) : null;
                isLoggedIn.value = true;
                // 设置请求头
                setAuthHeader(savedToken);
                // 验证 token 是否仍然有效
                validateToken();
            }
            catch (error) {
                console.error('Failed to restore auth state:', error);
                clearAuth();
            }
        }
    };
    // 设置认证头
    const setAuthHeader = (authToken) => {
        // 这里需要设置 axios 的默认请求头
        // 由于我们使用的是封装的 request，需要在 request.ts 中处理
        if (typeof window !== 'undefined') {
            localStorage.setItem('nps_token', authToken);
        }
    };
    // 验证 token 有效性
    const validateToken = async () => {
        try {
            await authApi.getProfile();
        }
        catch (error) {
            console.error('Token validation failed:', error);
            clearAuth();
        }
    };
    // 登录
    const login = async (loginData) => {
        try {
            const response = await authApi.login(loginData);
            if (response.code === 200) {
                const { token: authToken, user, permissions } = response.data;
                // 检查用户状态
                if (!user.status) {
                    ElMessage.error('账户已被禁用，请联系管理员');
                    return false;
                }
                // 保存状态
                token.value = authToken;
                userInfo.value = user;
                userPermissions.value = permissions;
                isLoggedIn.value = true;
                // 持久化存储
                localStorage.setItem('nps_token', authToken);
                localStorage.setItem('nps_user_info', JSON.stringify(user));
                localStorage.setItem('nps_user_permissions', JSON.stringify(permissions));
                // 设置请求头
                setAuthHeader(authToken);
                ElMessage.success('登录成功');
                return true;
            }
            else {
                // 根据错误码提供更具体的错误信息
                switch (response.code) {
                    case 401:
                        ElMessage.error('用户名或密码错误');
                        break;
                    case 403:
                        ElMessage.error('账户被禁用，请联系管理员');
                        break;
                    case 400:
                        ElMessage.error(response.message || '请求参数错误');
                        break;
                    default:
                        ElMessage.error(response.message || '登录失败');
                }
                return false;
            }
        }
        catch (error) {
            console.error('Login failed:', error);
            handleAuthError(error);
            return false;
        }
    };
    // 登出
    const logout = async () => {
        try {
            await authApi.logout();
        }
        catch (error) {
            console.error('Logout API failed:', error);
        }
        finally {
            clearAuth();
            ElMessage.success('已退出登录');
        }
    };
    // 清除认证状态
    const clearAuth = () => {
        token.value = '';
        userInfo.value = null;
        userPermissions.value = null;
        isLoggedIn.value = false;
        // 清除持久化存储
        localStorage.removeItem('nps_token');
        localStorage.removeItem('nps_user_info');
        localStorage.removeItem('nps_user_permissions');
    };
    // 更新用户信息
    const updateProfile = async (profileData) => {
        try {
            const response = await authApi.updateProfile(profileData);
            if (response.code === 200) {
                userInfo.value = response.data;
                localStorage.setItem('nps_user_info', JSON.stringify(response.data));
                ElMessage.success('个人信息更新成功');
                return true;
            }
            else {
                ElMessage.error(response.message || '更新失败');
                return false;
            }
        }
        catch (error) {
            console.error('Update profile failed:', error);
            ElMessage.error(error.response?.data?.message || '更新失败');
            return false;
        }
    };
    // 修改密码
    const changePassword = async (passwordData) => {
        try {
            const response = await authApi.changePassword(passwordData);
            if (response.code === 200) {
                ElMessage.success('密码修改成功');
                return true;
            }
            else {
                ElMessage.error(response.message || '密码修改失败');
                return false;
            }
        }
        catch (error) {
            console.error('Change password failed:', error);
            ElMessage.error(error.response?.data?.message || '密码修改失败');
            return false;
        }
    };
    // 刷新用户信息
    const refreshUserInfo = async () => {
        try {
            const response = await authApi.getProfile();
            if (response.code === 200) {
                userInfo.value = response.data;
                localStorage.setItem('nps_user_info', JSON.stringify(response.data));
            }
        }
        catch (error) {
            console.error('Refresh user info failed:', error);
        }
    };
    return {
        // 状态
        token,
        userInfo,
        isLoggedIn,
        userPermissions,
        // 计算属性
        isAdmin,
        isManager,
        userName,
        userAvatar,
        hasSystemManagementPermission,
        hasDeviceManagementPermission,
        hasGroupManagementPermission,
        managedGroups,
        canManageGroup,
        // 方法
        initAuth,
        login,
        logout,
        clearAuth,
        updateProfile,
        changePassword,
        refreshUserInfo,
        validateToken
    };
});
